// Operadores — quem opera este back office (ADR-013).
//
// Antes eles vinham só de `ATLAS_ADMIN_USUARIOS`, e cadastrar alguém exigia
// gerar hash à mão, editar um segredo e reiniciar o serviço. Numa operação com
// curadoria distribuída, esse atrito empurra para a senha compartilhada — e
// senha compartilhada faz todo mundo virar "admin" na auditoria, que é o que o
// passo 1.6 existiu para eliminar.
//
// A senha passa por aqui **uma vez** e sai derivada por `scrypt`. Não há como
// vê-la depois: quem esquecer recebe outra.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { alterar, enviar, obter, type Operador } from "../api.js";
import { Busca, casa } from "../pecas/Busca.js";
import { Carregando, Falha, Gaveta, quando, Vazio } from "../pecas/basicos.js";

export function Operadores({
  avisar,
  atual,
}: {
  avisar: (t: string, erro?: boolean) => void;
  atual: string;
}): ReactNode {
  const qc = useQueryClient();
  const [criando, setCriando] = useState(false);
  const [trocando, setTrocando] = useState<Operador | null>(null);
  const [busca, setBusca] = useState("");

  const lista = useQuery({
    queryKey: ["operadores"],
    queryFn: () => obter<Operador[]>("/operadores"),
  });

  const criar = useMutation({
    mutationFn: (corpo: Record<string, unknown>) => enviar<Operador>("/operadores", corpo),
    onSuccess: (o) => {
      avisar(`Operador "${o.usuario}" criado. A senha não pode ser vista depois.`);
      setCriando(false);
      void qc.invalidateQueries({ queryKey: ["operadores"] });
    },
    onError: (e: Error) => avisar(e.message, true),
  });

  const alterarOp = useMutation({
    mutationFn: ({ usuario, corpo }: { usuario: string; corpo: Record<string, unknown> }) =>
      alterar<Operador>(`/operadores/${usuario}`, corpo),
    onSuccess: (o, v) => {
      avisar(
        "senha" in v.corpo
          ? `Senha de "${o.usuario}" trocada. As sessões dela caíram — a chave deriva do hash.`
          : o.ativo
            ? `"${o.usuario}" reativado.`
            : `"${o.usuario}" desativado: não entra mais, e a assinatura na auditoria fica.`,
      );
      setTrocando(null);
      void qc.invalidateQueries({ queryKey: ["operadores"] });
    },
    onError: (e: Error) => avisar(e.message, true),
  });

  if (lista.isLoading) return <Carregando />;
  if (lista.error) return <Falha erro={lista.error} />;
  const todos = lista.data ?? [];
  const filtrados = todos.filter((o) => casa(`${o.usuario} ${o.nome}`, busca));

  return (
    <div className="rolagem">
      <div className="barra-tela">
        <Busca
          valor={busca}
          aoMudar={setBusca}
          rotulo="Buscar operador"
          achados={filtrados.length}
          total={todos.length}
        />
        <button className="btn btn-primario" onClick={() => setCriando(true)}>
          Novo operador…
        </button>
      </div>

      <div className="aviso">
        <div>
          <b>A promoção de versão é uma assinatura.</b>
          <p>
            Cada pessoa entra com o próprio usuário: <code className="mono">core.auditoria</code>{" "}
            grava quem respondeu por aquela versão selada. Com senha compartilhada, a assinatura não
            vale nada.
          </p>
        </div>
      </div>

      {todos.length === 0 ? (
        <Vazio>
          Nenhum operador cadastrado. Você entrou pela credencial de emergência — cadastre o
          primeiro operador para sair dela.
        </Vazio>
      ) : (
        <table className="tabela">
          <thead>
            <tr>
              <th>Usuário</th>
              <th>Situação</th>
              <th>Último acesso</th>
              <th>Cadastrado</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filtrados.map((o) => (
              <tr key={o.usuario}>
                <td>
                  <code className="mono">{o.usuario}</code>
                  <div className="discreto">
                    {o.nome}
                    {o.usuario === atual ? " · você" : ""}
                  </div>
                </td>
                <td>
                  <span className={`pilula ${o.ativo ? "pilula-publicado" : ""}`}>
                    {o.ativo ? "ativo" : "desativado"}
                  </span>
                </td>
                <td className="mono discreto">{o.ultimo_acesso ? quando(o.ultimo_acesso) : "—"}</td>
                <td className="discreto">
                  {quando(o.criado_em)}
                  <div className="discreto">por {o.criado_por}</div>
                </td>
                <td>
                  <div className="linha-botoes">
                    <button className="btn btn-pequeno" onClick={() => setTrocando(o)}>
                      Trocar senha
                    </button>
                    {/* Desativar a si mesmo é o jeito mais rápido de ficar de
                        fora do próprio back office — o servidor recusa, e a
                        tela nem oferece. */}
                    {o.usuario !== atual ? (
                      <button
                        className={`btn btn-pequeno ${o.ativo ? "btn-perigo" : ""}`}
                        disabled={alterarOp.isPending}
                        onClick={() =>
                          alterarOp.mutate({ usuario: o.usuario, corpo: { ativo: !o.ativo } })
                        }
                      >
                        {o.ativo ? "Desativar" : "Reativar"}
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {criando ? (
        <FormOperador
          aoFechar={() => setCriando(false)}
          aoSalvar={(corpo) => criar.mutate(corpo)}
          salvando={criar.isPending}
        />
      ) : null}

      {trocando ? (
        <FormOperador
          operador={trocando}
          aoFechar={() => setTrocando(null)}
          aoSalvar={(corpo) => alterarOp.mutate({ usuario: trocando.usuario, corpo })}
          salvando={alterarOp.isPending}
        />
      ) : null}
    </div>
  );
}

function FormOperador({
  operador,
  aoFechar,
  aoSalvar,
  salvando,
}: {
  operador?: Operador;
  aoFechar: () => void;
  aoSalvar: (corpo: Record<string, unknown>) => void;
  salvando: boolean;
}): ReactNode {
  const trocandoSenha = operador !== undefined;
  const [usuario, setUsuario] = useState("");
  const [nome, setNome] = useState("");
  const [senha, setSenha] = useState("");

  const usuarioValido = /^[a-z][a-z0-9._-]{1,40}$/.test(usuario);
  const senhaOk = senha.length >= 10;
  const pronto = senhaOk && (trocandoSenha || (usuarioValido && nome.trim() !== ""));

  return (
    <Gaveta
      titulo={trocandoSenha ? `Trocar senha de ${operador.usuario}` : "Novo operador"}
      subtitulo={trocandoSenha ? operador.nome : "quem vai operar o back office"}
      aoFechar={aoFechar}
    >
      {trocandoSenha ? null : (
        <div className="form-secao">
          <div className="form-secao-titulo">Identificação</div>
          <div className="campo">
            <label htmlFor="op-usuario">Usuário</label>
            <input
              id="op-usuario"
              value={usuario}
              placeholder="ana.curadora"
              onChange={(e) => setUsuario(e.target.value)}
            />
            <div
              className={`campo-ajuda ${usuario !== "" && !usuarioValido ? "campo-ajuda-erro" : ""}`}
            >
              Minúsculas, dígitos e <code className="mono">. _ -</code>. É o que aparece na
              auditoria como assinatura de quem promoveu.
            </div>
          </div>
          <div className="campo">
            <label htmlFor="op-nome">Nome</label>
            <input
              id="op-nome"
              value={nome}
              placeholder="Ana Curadora"
              onChange={(e) => setNome(e.target.value)}
            />
          </div>
        </div>
      )}

      <div className="form-secao">
        <div className="form-secao-titulo">Senha</div>
        <div className="campo">
          <label htmlFor="op-senha">{trocandoSenha ? "Nova senha" : "Senha"}</label>
          <input
            id="op-senha"
            type="password"
            autoComplete="new-password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
          />
          <div className={`campo-ajuda ${senha !== "" && !senhaOk ? "campo-ajuda-erro" : ""}`}>
            Ao menos 10 caracteres. Só comprimento é exigido: regra de composição produz{" "}
            <code className="mono">Senha@123</code> e dá sensação de segurança sem acrescentá-la.
          </div>
        </div>
        <p className="campo-ajuda">
          A senha não pode ser vista depois — o banco guarda só a derivação. Quem esquecer recebe
          outra.
          {trocandoSenha ? " Trocar a senha derruba as sessões abertas dessa pessoa." : ""}
        </p>
      </div>

      <div className="acoes-form">
        <button
          className="btn btn-primario"
          disabled={!pronto || salvando}
          onClick={() =>
            aoSalvar(
              trocandoSenha ? { senha } : { usuario: usuario.trim(), nome: nome.trim(), senha },
            )
          }
        >
          {salvando ? "Salvando…" : trocandoSenha ? "Trocar senha" : "Criar operador"}
        </button>
        <button className="btn" onClick={aoFechar}>
          Cancelar
        </button>
      </div>
    </Gaveta>
  );
}
